import type { FC } from 'react';
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import clsx from 'clsx';

import { pageNavigationButtonClasses } from './classes';
import { PaginationItems } from './PaginationItems';

export interface PaginationProps {
  onPageChange: (newPageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageIndex: number;
  pageSize: number;
  pageSizeOptions?: readonly number[];
  totalResults: number;
}

export const Pagination: FC<PaginationProps> = ({
  onPageChange,
  onPageSizeChange,
  pageIndex,
  pageSize,
  pageSizeOptions = [10, 20, 50],
  totalResults,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const totalPages = Math.ceil(totalResults / pageSize);

  const changePage = (newPageIndex: number) => onPageChange(newPageIndex);

  const handleNextPageClick = () => {
    if (pageIndex + 1 < totalPages) {
      onPageChange(pageIndex + 1);
    }
  };

  const handlePreviousPageClick = () => {
    if (pageIndex > 0) {
      onPageChange(pageIndex - 1);
    }
  };

  const handlePageSizeSelect = (size: number) => {
    onPageSizeChange?.(size);
    setIsOpen(false);
  };

  return (
    <div className="flex items-center justify-between pl-3">
      <div className={clsx('flex flex-1 items-center justify-between')}>
        <div className="flex items-center gap-6">
          {onPageSizeChange && (
            <div className="flex items-center gap-2.5">
              <p className="text-sm leading-5 font-normal text-gray-700">Rows per page</p>
              <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={clsx(
                      'border-info-300 text-info-500 relative inline-flex w-20 cursor-pointer items-center justify-between rounded-md border bg-white px-4 py-2 text-sm font-medium',
                      'hover:bg-info-50 focus:outline-none',
                    )}
                  >
                    <span>{pageSize}</span>
                    <ChevronDownIcon className={clsx('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto min-w-20 overflow-hidden rounded-md p-0">
                  <ul>
                    {pageSizeOptions.map((size) => {
                      const isSelected = size === pageSize;
                      return (
                        <li key={size}>
                          <button
                            type="button"
                            onClick={() => handlePageSizeSelect(size)}
                            className={clsx(
                              'border-info-300 relative inline-flex w-full cursor-pointer items-center justify-between border-t px-4 py-2 text-left text-sm font-medium',
                              isSelected
                                ? 'border-primary-500 bg-primary-50 text-primary-600 z-10'
                                : 'text-info-500 hover:bg-info-50 bg-white',
                              'first:border-t-0 focus:outline-none',
                            )}
                          >
                            <span>{size}</span>
                            {isSelected && <CheckIcon className="h-5 w-5" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </PopoverContent>
              </Popover>
            </div>
          )}
          <nav className="relative inline-flex -space-x-px rounded-md shadow-xs" aria-label="Pagination">
            <button
              onClick={handlePreviousPageClick}
              className={clsx(pageNavigationButtonClasses({ isPrevious: true }))}
              type="button"
              disabled={pageIndex === 0}
            >
              <span className="sr-only">Previous page</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <PaginationItems currentPageIndex={pageIndex} onPageChange={changePage} totalPages={totalPages} />
            <button
              onClick={handleNextPageClick}
              className={clsx(pageNavigationButtonClasses({ isNext: true }))}
              type="button"
              disabled={pageIndex + 1 >= totalPages}
            >
              <span className="sr-only">Next page</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};
